import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  matchCategoryColor,
  CategoryValue,
} from 'talent_matching/components/shared/match_category';
import type { MatchReport, MatchReportSkill } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.panel.match_report');

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const Category = ({ value }: { value?: string | null }) =>
  value ? (
    <CategoryValue metadata $color={matchCategoryColor(value)}>
      {value}
    </CategoryValue>
  ) : (
    <SecondaryBody metadata>{'--'}</SecondaryBody>
  );

const AttributeRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <SpaceBetween>
    <SecondaryBody>{label}</SecondaryBody>
    <Category value={value} />
  </SpaceBetween>
);

const SkillList = ({
  label,
  skills,
}: {
  label: string;
  skills?: MatchReportSkill[] | null;
}) => {
  if (!skills || skills.length === 0) {
    return null;
  }

  return (
    <Stack direction="column" spacing={1}>
      <SecondaryBody medium>{label}</SecondaryBody>
      {skills.map((skill, index) => (
        <SpaceBetween key={skill.skill_name ?? index}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
          >
            <SecondaryBody>{skill.skill_name}</SecondaryBody>
            {skill.attested_job && <Chip text={t('skills.attested_job')} />}
            {skill.attested_candidate && (
              <Chip text={t('skills.attested_candidate')} />
            )}
          </Stack>
          <Category value={skill.assessment_category} />
        </SpaceBetween>
      ))}
    </Stack>
  );
};

type Props = {
  matchReport?: MatchReport | null;
};

const MatchReportTabPanel = ({ matchReport }: Props) => {
  if (!matchReport) {
    return <SecondaryBody>{t('empty')}</SecondaryBody>;
  }

  const { attributes, education, skills } = matchReport;
  const hasEducation =
    education &&
    (education.job_degree_min ||
      education.candidate_degree_max ||
      education.meets_requirement);

  return (
    <Stack direction="column" spacing={2}>
      <Body medium>{t('title')}</Body>

      {attributes && (
        <>
          <Stack direction="column" spacing={1}>
            <SecondaryBody medium>{t('attributes.title')}</SecondaryBody>
            <AttributeRow
              label={t('attributes.skills')}
              value={attributes.skills_category}
            />
            <AttributeRow
              label={t('attributes.experience')}
              value={attributes.work_history_category}
            />
            <AttributeRow
              label={t('attributes.education')}
              value={attributes.education_category}
            />
          </Stack>
          <Divider />
        </>
      )}

      {hasEducation && (
        <>
          <Stack direction="column" spacing={1}>
            <SecondaryBody medium>{t('education.title')}</SecondaryBody>
            {education?.job_degree_min && (
              <SpaceBetween>
                <SecondaryBody>{t('education.job_requirement')}</SecondaryBody>
                <SecondaryBody>{education.job_degree_min}</SecondaryBody>
              </SpaceBetween>
            )}
            {education?.candidate_degree_max && (
              <SpaceBetween>
                <SecondaryBody>{t('education.candidate')}</SecondaryBody>
                <SecondaryBody>{education.candidate_degree_max}</SecondaryBody>
              </SpaceBetween>
            )}
            {education?.meets_requirement && (
              <SpaceBetween>
                <SecondaryBody>
                  {t('education.meets_requirement')}
                </SecondaryBody>
                <SecondaryBody>{education.meets_requirement}</SecondaryBody>
              </SpaceBetween>
            )}
          </Stack>
          <Divider />
        </>
      )}

      {skills && (
        <>
          <SkillList label={t('skills.technical')} skills={skills.technical} />
          <SkillList label={t('skills.baseline')} skills={skills.baseline} />
        </>
      )}
    </Stack>
  );
};

export default MatchReportTabPanel;
